import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, IconButton, Skeleton, Stack, useTheme } from "@mui/material";
import PieChartOutlineRoundedIcon from "@mui/icons-material/PieChartOutlineRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { isValidBuyerOrder, money, orderHasStatus, toDate } from "./dashboardData";

// Generate smooth cubic bezier SVG path from array of [x, y] coordinates
function getSmoothPath(points) {
  if (!points || points.length === 0) return "";
  if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;

  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;

    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

function getOrderItemImage(item = {}) {
  const candidates = [
    item.imageUrl,
    item.image,
    Array.isArray(item.images) ? item.images[0] : null,
    item.product?.imageUrl,
    item.product?.image,
    Array.isArray(item.product?.images) ? item.product.images[0] : null,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate) return candidate;
    if (candidate && typeof candidate === "object") {
      const url = candidate.url || candidate.src || candidate.downloadURL;
      if (url) return url;
    }
  }

  return "";
}

// 1. REVENUE & ORDERS TREND CHART
export function RevenueTrendChart({ orders = [], loading = false, error = false }) {
  const [timeRange, setTimeRange] = useState("30d"); // "7d", "30d", "6m"
  const [metric, setMetric] = useState("revenue"); // "revenue", "orders"
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const chartData = useMemo(() => {
    const now = new Date();
    const dataPoints = [];

    if (timeRange === "7d") {
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const dateKey = d.toISOString().split("T")[0];
        const label = d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" });
        dataPoints.push({ key: dateKey, label, date: d, revenue: 0, count: 0 });
      }
    } else if (timeRange === "30d") {
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const dateKey = d.toISOString().split("T")[0];
        const label = d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
        dataPoints.push({ key: dateKey, label, date: d, revenue: 0, count: 0 });
      }
    } else {
      // 6 months
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const label = d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
        dataPoints.push({ key: dateKey, label, date: d, revenue: 0, count: 0 });
      }
    }

    // Populate data from orders
    orders.forEach((order) => {
      const orderDate = toDate(order.createdAt || order.modifiedAt);
      if (!orderDate) return;
      const amt = Number.isFinite(Number(order.amount)) ? Number(order.amount) : 0;

      if (timeRange === "6m") {
        const monthKey = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, "0")}`;
        const match = dataPoints.find((dp) => dp.key === monthKey);
        if (match) {
          match.revenue += amt;
          match.count += 1;
        }
      } else {
        const dayKey = orderDate.toISOString().split("T")[0];
        const match = dataPoints.find((dp) => dp.key === dayKey);
        if (match) {
          match.revenue += amt;
          match.count += 1;
        }
      }
    });

    return dataPoints;
  }, [orders, timeRange]);

  const totalRevenue = chartData.reduce((sum, d) => sum + d.revenue, 0);
  const totalOrders = chartData.reduce((sum, d) => sum + d.count, 0);
  const avgRevenuePerPeriod = chartData.length ? Math.round(totalRevenue / chartData.length) : 0;

  // Chart dimensions & scaling
  const width = 640;
  const height = 220;
  const padding = { top: 20, right: 20, bottom: 35, left: 50 };
  const graphWidth = width - padding.left - padding.right;
  const graphHeight = height - padding.top - padding.bottom;

  const values = chartData.map((d) => (metric === "revenue" ? d.revenue : d.count));
  const maxValue = Math.max(...values, metric === "revenue" ? 1000 : 5);

  const points = chartData.map((d, index) => {
    const x = padding.left + (index / (chartData.length - 1 || 1)) * graphWidth;
    const val = metric === "revenue" ? d.revenue : d.count;
    const y = padding.top + graphHeight - (val / maxValue) * graphHeight;
    return [x, y, d];
  });

  const pathD = getSmoothPath(points.map(([x, y]) => [x, y]));
  const areaD = points.length > 0
    ? `${pathD} L ${points[points.length - 1][0]} ${padding.top + graphHeight} L ${points[0][0]} ${padding.top + graphHeight} Z`
    : "";

  return (
    <div className="admin-dashboard__panel admin-dashboard__chart-panel">
      <div className="admin-dashboard__panel-heading">
        <div>
          <h2>Revenue & Order Trends</h2>
          <p>Sales trajectory and order volume over time</p>
        </div>
        <div className="admin-dashboard__chart-controls">
          <div className="admin-dashboard__toggle-group">
            <button
              type="button"
              className={metric === "revenue" ? "active" : ""}
              onClick={() => setMetric("revenue")}
            >
              Revenue
            </button>
            <button
              type="button"
              className={metric === "orders" ? "active" : ""}
              onClick={() => setMetric("orders")}
            >
              Orders
            </button>
          </div>
          <div className="admin-dashboard__toggle-group">
            <button
              type="button"
              className={timeRange === "7d" ? "active" : ""}
              onClick={() => setTimeRange("7d")}
            >
              7D
            </button>
            <button
              type="button"
              className={timeRange === "30d" ? "active" : ""}
              onClick={() => setTimeRange("30d")}
            >
              30D
            </button>
            <button
              type="button"
              className={timeRange === "6m" ? "active" : ""}
              onClick={() => setTimeRange("6m")}
            >
              6M
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
      ) : error ? (
        <p className="admin-dashboard__empty" style={{ padding: "40px 10px" }}>
          Chart data unavailable.
        </p>
      ) : (
        <>
          <div className="admin-dashboard__chart-stats">
            <div>
              <span>Period Revenue</span>
              <strong>{money(totalRevenue)}</strong>
            </div>
            <div>
              <span>Period Orders</span>
              <strong>{totalOrders} orders</strong>
            </div>
            <div>
              <span>{timeRange === "6m" ? "Monthly" : "Daily"} Avg</span>
              <strong>{money(avgRevenuePerPeriod)}</strong>
            </div>
          </div>

          <div className="admin-dashboard__svg-container" style={{ position: "relative" }}>
            <svg
              key={`${metric}-${timeRange}`}
              viewBox={`0 0 ${width} ${height}`}
              className="admin-dashboard__svg-graph"
              preserveAspectRatio="none"
              style={{ width: "100%", height: "220px", overflow: "visible" }}
            >
              <defs>
                <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                const y = padding.top + graphHeight * (1 - ratio);
                const val = Math.round(maxValue * ratio);
                return (
                  <g key={i}>
                    <line
                      x1={padding.left}
                      y1={y}
                      x2={width - padding.right}
                      y2={y}
                      stroke="var(--dash-border)"
                      strokeDasharray="3 3"
                      strokeWidth="1"
                    />
                    <text
                      x={padding.left - 8}
                      y={y + 4}
                      textAnchor="end"
                      fontSize="10"
                      fill="var(--dash-muted)"
                      fontFamily="inherit"
                    >
                      {metric === "revenue" ? (val >= 1000 ? `₹${(val / 1000).toFixed(0)}k` : `₹${val}`) : val}
                    </text>
                  </g>
                );
              })}

              {/* Area & Line */}
              {areaD && (
                <path
                  d={areaD}
                  fill="url(#chartGradient)"
                  className="admin-dashboard__trend-area"
                />
              )}
              {pathD && (
                <path
                  d={pathD}
                  pathLength="1"
                  className="admin-dashboard__trend-line"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Interactive Points */}
              {points.map(([x, y, data], idx) => {
                // Show label every few steps on 30d
                const showLabel =
                  timeRange === "7d" ||
                  timeRange === "6m" ||
                  idx % 5 === 0 ||
                  idx === points.length - 1;

                return (
                  <g key={idx}>
                    {showLabel && (
                      <text
                        x={x}
                        y={height - 8}
                        textAnchor="middle"
                        fontSize="10"
                        fill="var(--dash-muted)"
                        fontFamily="inherit"
                      >
                        {data.label}
                      </text>
                    )}
                    <circle
                      className="admin-dashboard__trend-point"
                      cx={x}
                      cy={y}
                      r={hoveredPoint?.key === data.key ? 5.5 : 3}
                      fill={hoveredPoint?.key === data.key ? "#059669" : "#ffffff"}
                      stroke="#10b981"
                      strokeWidth={hoveredPoint?.key === data.key ? 3 : 2}
                      style={{
                        cursor: "pointer",
                        transition: "r 0.15s ease",
                        animationDelay: `${320 + idx * 24}ms`,
                      }}
                      onMouseEnter={() => setHoveredPoint({ ...data, x, y })}
                      onMouseLeave={() => setHoveredPoint(null)}
                    />
                  </g>
                );
              })}
            </svg>

            {/* Hover Tooltip */}
            {hoveredPoint && (
              <div
                className="admin-dashboard__chart-tooltip"
                style={{
                  left: `${(hoveredPoint.x / width) * 100}%`,
                  top: `${(hoveredPoint.y / height) * 100}%`,
                }}
              >
                <div className="tooltip-title">{hoveredPoint.label}</div>
                <div className="tooltip-body">
                  <span>Revenue: <strong>{money(hoveredPoint.revenue)}</strong></span>
                  <span>Orders: <strong>{hoveredPoint.count}</strong></span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// 2. ORDER STATUS DISTRIBUTION (DONUT CHART)
const orderStatusConfig = [
  { label: "Delivered", color: "#10b981", statuses: ["Delivered"] },
  { label: "On the way", color: "#3b82f6", statuses: ["Shipped", "Out for delivery"] },
  { label: "Awaiting shipment", color: "#f59e0b", statuses: ["Placed"] },
  { label: "Cancelled", color: "#ef4444", statuses: ["Cancelled by buyer", "Cancelled by seller"] },
  { label: "Refunded", color: "#8b5cf6", statuses: ["Refunded"] },
];

export function OrderStatusDonut({ orders = [], loading = false, error = false }) {
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const totalOrders = orders.length;

  const distribution = useMemo(() => {
    let accumulatedAngle = 0;
    return orderStatusConfig.map((item) => {
      const count = orders.filter((order) =>
        orderHasStatus(order, item.statuses)
      ).length;
      const percent = totalOrders > 0 ? (count / totalOrders) * 100 : 0;
      const angle = (percent / 100) * 360;
      const startAngle = accumulatedAngle;
      accumulatedAngle += angle;
      return { ...item, count, percent, startAngle, angle };
    });
  }, [orders, totalOrders]);

  // SVG Donut calculation
  const radius = 58;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * radius; // ~364.4

  let accumulatedPercent = 0;

  return (
    <div className="admin-dashboard__panel admin-dashboard__donut-panel">
      <div className="admin-dashboard__panel-heading">
        <div>
          <h2>Order Fulfillment</h2>
          <p>Status breakdown across catalog</p>
        </div>
        <PieChartOutlineRoundedIcon sx={{ color: "var(--dash-accent)" }} />
      </div>

      {loading ? (
        <Skeleton variant="circular" width={180} height={180} sx={{ margin: "auto" }} />
      ) : error ? (
        <div className="admin-dashboard__empty" style={{ padding: "40px 10px" }}>
          <p>Order fulfillment data unavailable.</p>
        </div>
      ) : totalOrders === 0 ? (
        <div className="admin-dashboard__empty" style={{ padding: "40px 10px" }}>
          <p>No orders yet to graph.</p>
        </div>
      ) : (
        <div className="admin-dashboard__donut-wrap">
          <div className="admin-dashboard__donut-svg-box">
            <svg viewBox="0 0 160 160" className="admin-dashboard__donut-svg">
              <circle
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                stroke="var(--dash-soft)"
                strokeWidth={strokeWidth}
              />
              {distribution.map((item, idx) => {
                if (item.count === 0) return null;
                const strokeDasharray = `${(item.percent / 100) * circumference} ${circumference}`;
                const strokeDashoffset = -((accumulatedPercent / 100) * circumference);
                accumulatedPercent += item.percent;

                const isHovered = hoveredSlice?.label === item.label;

                return (
                  <circle
                    key={idx}
                    className="admin-dashboard__donut-segment"
                    cx="80"
                    cy="80"
                    r={radius}
                    fill="none"
                    stroke={item.color}
                    strokeWidth={isHovered ? strokeWidth + 4 : strokeWidth}
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    transform="rotate(-90 80 80)"
                    style={{
                      transition: "all 0.25s ease",
                      cursor: "pointer",
                      opacity: hoveredSlice && !isHovered ? 0.45 : 1,
                      animationDelay: `${idx * 90}ms`,
                    }}
                    onMouseEnter={() => setHoveredSlice(item)}
                    onMouseLeave={() => setHoveredSlice(null)}
                  />
                );
              })}
            </svg>
            <div className="admin-dashboard__donut-center">
              <span className="donut-center-value">
                {hoveredSlice ? hoveredSlice.count : totalOrders}
              </span>
              <span className="donut-center-label">
                {hoveredSlice ? hoveredSlice.label : "Total Orders"}
              </span>
            </div>
          </div>

          {/* Legend */}
          <div className="admin-dashboard__donut-legend">
            {distribution.map((item) => (
              <div
                key={item.label}
                className={`legend-row ${hoveredSlice?.label === item.label ? "hovered" : ""}`}
                onMouseEnter={() => setHoveredSlice(item)}
                onMouseLeave={() => setHoveredSlice(null)}
              >
                <div className="legend-left">
                  <span className="legend-dot" style={{ backgroundColor: item.color }} />
                  <span className="legend-label">{item.label}</span>
                </div>
                <div className="legend-right">
                  <strong>{item.count}</strong>
                  <span>({Math.round(item.percent)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// 3. CATALOG CATEGORIES BAR GRAPH
export function CategoryBarChart({ products = [], loading = false, error = false }) {
  const categoryStats = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      const cat = p.parentCategory?.name || (p.categories && p.categories[0]) || "General";
      const cleanCat = cat.replace(/_/g, " ");
      map[cleanCat] = (map[cleanCat] || 0) + 1;
    });

    const list = Object.entries(map).map(([name, count]) => ({
      name,
      count,
      percent: products.length > 0 ? (count / products.length) * 100 : 0,
    }));

    return list.sort((a, b) => b.count - a.count).slice(0, 5);
  }, [products]);

  const maxCount = Math.max(...categoryStats.map((c) => c.count), 1);

  return (
    <div className="admin-dashboard__panel admin-dashboard__categories-panel">
      <div className="admin-dashboard__panel-heading">
        <div>
          <h2>Catalog by Category</h2>
          <p>Top product volume by category</p>
        </div>
        <BarChartRoundedIcon sx={{ color: "var(--dash-accent)" }} />
      </div>

      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton height={32} />
          <Skeleton height={32} />
          <Skeleton height={32} />
        </Stack>
      ) : error ? (
        <div className="admin-dashboard__empty" style={{ padding: "40px 10px" }}>
          <p>Category data unavailable.</p>
        </div>
      ) : categoryStats.length === 0 ? (
        <div className="admin-dashboard__empty" style={{ padding: "40px 10px" }}>
          <p>No products categorized yet.</p>
        </div>
      ) : (
        <div className="admin-dashboard__bars-list">
          {categoryStats.map((cat, idx) => {
            const barWidth = Math.max(10, Math.round((cat.count / maxCount) * 100));
            return (
              <div key={idx} className="category-bar-row">
                <div className="category-bar-info">
                  <span className="category-name">{cat.name}</span>
                  <span className="category-counts">
                    <strong>{cat.count} items</strong> ({Math.round(cat.percent)}%)
                  </span>
                </div>
                <div className="category-bar-track">
                  <div
                    className="category-bar-fill"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: idx === 0 ? "#10b981" : idx === 1 ? "#059669" : "#34d399",
                      animationDelay: `${idx * 90}ms`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 4. MOST ORDERED PRODUCTS
export function TopProductsChart({ orders = [], products = [], loading = false, error = false }) {
  const [metric, setMetric] = useState("units"); // "units" or "revenue"

  const topProducts = useMemo(() => {
    const prodMap = new Map();
    (products || []).forEach((p) => {
      if (p.id) prodMap.set(p.id, p);
      if (p.title) prodMap.set(p.title.toLowerCase().trim(), p);
    });

    const itemAgg = new Map();

    (orders || []).filter(isValidBuyerOrder).forEach((order) => {
      const items = Array.isArray(order.items) ? order.items : [];
      items.forEach((item) => {
        const id = item.id || item.productId || item.title || "item";
        const title = item.title || item.name || "Unnamed Product";
        const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
        const price = Number(item.listPrice || item.price || 0);
        const itemRevenue = price > 0 ? price * quantity : 0;

        const matchingProd = prodMap.get(id) || prodMap.get(title.toLowerCase().trim());
        const imageUrl =
          item.images?.[0]?.url ||
          (typeof item.images?.[0] === "string" ? item.images[0] : null) ||
          matchingProd?.images?.[0]?.url ||
          (typeof matchingProd?.images?.[0] === "string" ? matchingProd.images[0] : "");
        const category =
          matchingProd?.parentCategory?.name ||
          (matchingProd?.categories && matchingProd.categories[0]) ||
          item.category ||
          "";

        if (!itemAgg.has(id)) {
          itemAgg.set(id, {
            id,
            title,
            imageUrl,
            category: category ? category.replace(/_/g, " ") : "",
            units: 0,
            orderCount: 0,
            revenue: 0,
          });
        }

        const existing = itemAgg.get(id);
        existing.units += quantity;
        existing.orderCount += 1;
        existing.revenue += itemRevenue;
        if (!existing.imageUrl && imageUrl) existing.imageUrl = imageUrl;
      });
    });

    const list = Array.from(itemAgg.values());
    list.sort((a, b) => (metric === "units" ? b.units - a.units : b.revenue - a.revenue));
    return list.slice(0, 5);
  }, [orders, products, metric]);

  const maxVal = Math.max(...topProducts.map((p) => (metric === "units" ? p.units : p.revenue)), 1);

  return (
    <div className="admin-dashboard__panel admin-dashboard__top-products-panel">
      <div className="admin-dashboard__panel-heading admin-dashboard__top-products-heading">
        <div className="admin-dashboard__top-products-title">
          <span className="admin-dashboard__spotlight-icon" aria-hidden="true">
            <EmojiEventsRoundedIcon />
          </span>
          <div>
            <span className="admin-dashboard__panel-kicker">Sales leaders</span>
            <h2>Top Selling Products</h2>
            <p>Best sellers by volume and revenue</p>
          </div>
        </div>
        <div className="admin-dashboard__chart-controls">
          <div className="admin-dashboard__toggle-group">
            <button
              type="button"
              className={metric === "units" ? "active" : ""}
              onClick={() => setMetric("units")}
            >
              Units Sold
            </button>
            <button
              type="button"
              className={metric === "revenue" ? "active" : ""}
              onClick={() => setMetric("revenue")}
            >
              Revenue
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <Stack spacing={2}>
          <Skeleton height={42} />
          <Skeleton height={42} />
          <Skeleton height={42} />
          <Skeleton height={42} />
        </Stack>
      ) : error ? (
        <div className="admin-dashboard__empty" style={{ padding: "40px 10px" }}>
          <p>Product performance data unavailable.</p>
        </div>
      ) : topProducts.length === 0 ? (
        <div className="admin-dashboard__empty" style={{ padding: "40px 10px" }}>
          <p>No products ordered yet.</p>
        </div>
      ) : (
        <div className="admin-dashboard__top-products-list">
          {topProducts.map((product, idx) => {
            const barWidth = Math.max(
              8,
              Math.round(((metric === "units" ? product.units : product.revenue) / maxVal) * 100)
            );
            const rank = idx + 1;
            const rankBadgeClass =
              rank === 1
                ? "rank-gold"
                : rank === 2
                ? "rank-silver"
                : rank === 3
                ? "rank-bronze"
                : "rank-default";

            return (
              <div
                key={`${metric}-${product.id || idx}`}
                className={`top-product-item ${rank === 1 ? "top-product-item--featured" : ""}`}
                style={{ animationDelay: `${idx * 70}ms` }}
              >
                <div className="top-product-main">
                  <span className={`top-product-rank ${rankBadgeClass}`}>
                    #{rank}
                  </span>
                  <div className="top-product-thumb">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.title} />
                    ) : (
                      <ShoppingBagOutlinedIcon sx={{ fontSize: 18, color: "var(--dash-muted)" }} />
                    )}
                  </div>
                  <div className="top-product-info">
                    <span className="top-product-title" title={product.title}>
                      {product.title}
                    </span>
                    {product.category && (
                      <span className="top-product-cat">{product.category}</span>
                    )}
                  </div>
                  <div className="top-product-stats">
                    <span className="top-product-metric">
                      {metric === "units" ? (
                        <><strong>{product.units}</strong> units</>
                      ) : (
                        <strong>{money(product.revenue)}</strong>
                      )}
                    </span>
                    <span className="top-product-sub">
                      {product.orderCount} {product.orderCount === 1 ? "order" : "orders"}
                      {metric === "units" && product.revenue > 0 && ` · ${money(product.revenue)}`}
                    </span>
                  </div>
                </div>

                <div className="top-product-progress">
                  <div
                    className="top-product-fill"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: rank === 1 ? "#10b981" : rank === 2 ? "#059669" : "#34d399",
                      animationDelay: `${120 + idx * 70}ms`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// 5. CUSTOMERS WITH THE MOST ORDERS
export function TopCustomersChart({ orders = [], users = [], loading = false, error = false }) {
  const theme = useTheme();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const topCustomers = useMemo(() => {
    const customers = new Map();
    const registeredUsers = new Map();

    (users || []).forEach((user) => {
      if (user.id) registeredUsers.set(String(user.id), user);
      if (user.uid) registeredUsers.set(String(user.uid), user);
      if (user.email) registeredUsers.set(String(user.email).trim().toLowerCase(), user);
    });

    (orders || []).forEach((order, index) => {
      const user = order.user && typeof order.user === "object" ? order.user : {};
      const address = order.address && typeof order.address === "object" ? order.address : {};
      const email = String(user.email || address.email || "").trim();
      const mobile = String(user.mobile || user.phone || address.mobile || address.phone || "").trim();
      const name = String(user.name || address.name || "Guest customer").trim();
      const identity = user.id || user.uid || email.toLowerCase() || mobile || `guest-${order.id || index}`;
      const registeredUser = registeredUsers.get(String(user.id || user.uid || "")) || registeredUsers.get(email.toLowerCase());

      if (!customers.has(identity)) {
        customers.set(identity, {
          id: identity,
          name,
          email,
          mobile,
          joinedAt: registeredUser?.createdAt || registeredUser?.joinedAt || registeredUser?.metadata?.creationTime || user.createdAt,
          orderCount: 0,
          spend: 0,
          orders: [],
        });
      }

      const customer = customers.get(identity);
      customer.orderCount += 1;
      customer.spend += Number.isFinite(Number(order.amount)) ? Number(order.amount) : 0;
      customer.orders.push(order);
      if (customer.name === "Guest customer" && name !== "Guest customer") customer.name = name;
      if (!customer.email && email) customer.email = email;
      if (!customer.mobile && mobile) customer.mobile = mobile;
      if (!customer.joinedAt && registeredUser) {
        customer.joinedAt = registeredUser.createdAt || registeredUser.joinedAt || registeredUser.metadata?.creationTime;
      }
    });

    return Array.from(customers.values())
      .map((customer) => ({
        ...customer,
        orders: customer.orders.sort((a, b) => {
          const dateA = toDate(a.createdAt || a.modifiedAt)?.getTime() || 0;
          const dateB = toDate(b.createdAt || b.modifiedAt)?.getTime() || 0;
          return dateB - dateA;
        }),
      }))
      .sort((a, b) => b.orderCount - a.orderCount || b.spend - a.spend || a.name.localeCompare(b.name))
      .slice(0, 5);
  }, [orders, users]);

  const maxOrders = Math.max(...topCustomers.map((customer) => customer.orderCount), 1);

  return (
    <div className="admin-dashboard__panel admin-dashboard__top-customers-panel">
      <div className="admin-dashboard__panel-heading admin-dashboard__top-customers-heading">
        <div className="admin-dashboard__top-products-title">
          <span className="admin-dashboard__customer-icon" aria-hidden="true">
            <PeopleAltOutlinedIcon />
          </span>
          <div>
            <span className="admin-dashboard__panel-kicker">Customer loyalty</span>
            <h2>Customers Who Ordered Most</h2>
            <p>Ranked by completed checkout count, with lifetime order value</p>
          </div>
        </div>
        {!loading && !error && topCustomers.length > 0 && (
          <span className="admin-dashboard__customer-count">Top {topCustomers.length}</span>
        )}
      </div>

      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton height={54} />
          <Skeleton height={54} />
          <Skeleton height={54} />
        </Stack>
      ) : error ? (
        <div className="admin-dashboard__empty">
          <p>Customer order data unavailable.</p>
        </div>
      ) : topCustomers.length === 0 ? (
        <div className="admin-dashboard__empty">
          <p>No customer orders yet.</p>
        </div>
      ) : (
        <div className="admin-dashboard__top-customers-list">
          {topCustomers.map((customer, index) => {
            const rank = index + 1;
            const rankBadgeClass = rank === 1
              ? "rank-gold"
              : rank === 2
              ? "rank-silver"
              : rank === 3
              ? "rank-bronze"
              : "rank-default";
            const initials = customer.name
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0])
              .join("")
              .toUpperCase() || "GC";

            return (
              <button
                type="button"
                key={customer.id}
                className="top-customer-item"
                style={{ animationDelay: `${index * 70}ms` }}
                onClick={() => setSelectedCustomer(customer)}
                aria-label={`View details for ${customer.name}`}
              >
                <span className={`top-product-rank ${rankBadgeClass}`}>#{rank}</span>
                <span className="top-customer-avatar" aria-hidden="true">{initials}</span>
                <div className="top-customer-info">
                  <strong title={customer.name}>{customer.name}</strong>
                  <span title={customer.email || customer.mobile || undefined}>
                    {customer.email || customer.mobile || "Contact not available"}
                  </span>
                  <div className="top-customer-progress" aria-hidden="true">
                    <span
                      style={{
                        width: `${Math.max(8, Math.round((customer.orderCount / maxOrders) * 100))}%`,
                        animationDelay: `${120 + index * 70}ms`,
                      }}
                    />
                  </div>
                </div>
                <div className="top-customer-stats">
                  <strong>{customer.orderCount} {customer.orderCount === 1 ? "order" : "orders"}</strong>
                  <span>{money(customer.spend)} total value</span>
                </div>
                <ChevronRightRoundedIcon className="top-customer-open-icon" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      )}

      <Dialog
        open={Boolean(selectedCustomer)}
        onClose={() => setSelectedCustomer(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          className: "admin-dashboard__customer-dialog",
          "data-theme": theme.palette.mode,
          sx: {
            "--dash-surface": theme.palette.mode === "dark" ? "#1c3029" : "#fff",
            "--dash-text": theme.palette.mode === "dark" ? "#edf6f1" : "#193b34",
            "--dash-muted": theme.palette.mode === "dark" ? "#b0c6bb" : "#65756f",
            "--dash-border": theme.palette.mode === "dark" ? "#365047" : "#e3eae6",
            "--dash-soft": theme.palette.mode === "dark" ? "#243c32" : "#f3f7f5",
            "--dash-accent": theme.palette.mode === "dark" ? "#9dd8ba" : "#17664f",
          },
        }}
        aria-labelledby="customer-detail-title"
      >
        {selectedCustomer && (
          <>
            <div className="customer-detail-header">
              <div className="customer-detail-identity">
                <div>
                  <h2 id="customer-detail-title">{selectedCustomer.name}</h2>
                  <p>
                    {selectedCustomer.joinedAt && toDate(selectedCustomer.joinedAt)
                      ? `Joined ${toDate(selectedCustomer.joinedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                      : "Join date unavailable"}
                  </p>
                </div>
              </div>
              <IconButton aria-label="Close customer details" onClick={() => setSelectedCustomer(null)}>
                <CloseRoundedIcon />
              </IconButton>
            </div>
            <DialogContent className="customer-detail-content">
              {(selectedCustomer.email || selectedCustomer.mobile) && (
                <dl className="customer-detail-contact">
                  {selectedCustomer.email && <div><dt>Email</dt><dd>{selectedCustomer.email}</dd></div>}
                  {selectedCustomer.mobile && <div><dt>Phone</dt><dd>{selectedCustomer.mobile}</dd></div>}
                </dl>
              )}

              <div className="customer-detail-stats" aria-label="Customer order summary">
                <div><span>Orders</span><strong>{selectedCustomer.orderCount}</strong></div>
                <div><span>Total value</span><strong>{money(selectedCustomer.spend)}</strong></div>
                <div>
                  <span>Last order</span>
                  <strong>
                    {(() => {
                      const lastOrderDate = toDate(
                        selectedCustomer.orders[0]?.createdAt || selectedCustomer.orders[0]?.modifiedAt
                      );
                      return lastOrderDate
                        ? lastOrderDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                        : "Date unavailable";
                    })()}
                  </strong>
                </div>
              </div>

              <div className="customer-detail-orders">
                <div className="customer-detail-section-heading">
                  <h3>Recent orders</h3>
                  <span>Latest {Math.min(selectedCustomer.orders.length, 5)}</span>
                </div>
                {selectedCustomer.orders.slice(0, 5).map((order, index) => {
                  const orderDate = toDate(order.createdAt || order.modifiedAt);
                  const items = Array.isArray(order.items) ? order.items : [];
                  const orderLabel = order.id || `Order ${selectedCustomer.orders.length - index}`;
                  return (
                    <div className="customer-detail-order-block" key={order.id || index}>
                      <div className="customer-detail-order">
                        <div>
                          <strong>#{orderLabel}</strong>
                          <span>{orderDate ? orderDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Date unavailable"}</span>
                        </div>
                        <span className="customer-detail-status">{order.status || "Order placed"}</span>
                        <strong>{money(Number(order.amount) || 0)}</strong>
                      </div>
                      {items.length > 0 ? (
                        <div className="customer-detail-items" aria-label={`Items in order ${orderLabel}`}>
                          {items.map((item, itemIndex) => {
                            const title = item.title || item.name || "Unnamed product";
                            const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
                            const price = Number(item.sellingPrice || item.listPrice || item.price || 0);
                            const imageUrl = getOrderItemImage(item);

                            return (
                              <div className="customer-detail-product" key={item.id || item.productId || itemIndex}>
                                <span className="customer-detail-product-image">
                                  {imageUrl ? (
                                    <img src={imageUrl} alt={title} />
                                  ) : (
                                    <ShoppingBagOutlinedIcon aria-hidden="true" />
                                  )}
                                </span>
                                <div>
                                  <strong title={title}>{title}</strong>
                                  <span>
                                    {quantity} {quantity === 1 ? "unit" : "units"} × {money(price)}
                                    {(item.size || item.volume || item.weight) && ` · ${item.size || item.volume || item.weight}`}
                                  </span>
                                </div>
                                <strong>{money(price * quantity)}</strong>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="customer-detail-no-items">No item details available for this order.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
}
